import React, { useState, useRef, useCallback } from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

interface InfoTooltipProps {
  content: React.ReactNode;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ content }) => {
  const [open, setOpen] = useState(false);
  const isHovering = useRef(false);

  // On desktop (hover active), ignore Popover's click-based toggling.
  // On mobile (no hover events), let Popover handle tap open/close normally.
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (isHovering.current) return;
    setOpen(newOpen);
  }, []);

  // Use pointer events to distinguish real mouse hover from touch-synthesized events
  const handlePointerEnter = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    isHovering.current = true;
    setOpen(true);
  }, []);

  const handlePointerLeave = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    isHovering.current = false;
    setOpen(false);
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <span
          className="inline-flex items-center justify-center ml-2 cursor-help"
          role="button"
          tabIndex={0}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        className="max-w-[250px] text-sm"
        sideOffset={4}
        collisionPadding={12}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
};

export default InfoTooltip;
